import React from 'react';
import {
	Alert,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { safelyParseDate } from '@/utils/date';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useGetDebtorAutomationQuery } from '@/features/deals/debtorAutomationApi';
import { formatDate } from '@/utils/constants';
import Channel from '@/components/views/DebtorsView/DebtorAutomationPanel/Channel';
import ReminderStatusChip from '@/components/views/DebtorsView/DebtorAutomationPanel/ReminderStatusChip';
import DebtorRemindersBanner from '@/components/layouts/DebtorRemindersBanner';

const formatScheduledFor = (scheduledFor) => {
	const parsedDate = safelyParseDate(scheduledFor);

	if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
		return 'Unavailable';
	}

	return formatDate(parsedDate, true);
};

/**
 * Displays the upcoming reminder schedule for an enabled debtor automation deal.
 */
export const DebtorRemindersSection = () => {
	const { dealId } = useObjectContext();

	const { data: automation } = useGetDebtorAutomationQuery({ dealId }, { skip: !dealId });

	// list includes sent reminders and failure reasons. upcoming only includes scheduled
	// jobs list can miss. The same id can be in both, so keep one row per id.
	const remindersById = new Map(
		(automation?.reminders?.list ?? []).map((reminder) => [reminder.id, reminder]),
	);
	for (const reminder of automation?.reminders?.upcoming ?? []) {
		if (!remindersById.has(reminder.id)) {
			remindersById.set(reminder.id, reminder);
		}
	}
	const reminders = [...remindersById.values()].sort((left, right) =>
		left.scheduledFor.localeCompare(right.scheduledFor),
	);
	const error = Boolean(automation?.reminders?.error);
	const automationEnabled = Boolean(automation?.automationEnabled);

	if (!automationEnabled) {
		// Automation being off shouldn't hide an unresolved reminder failure that
		// still needs manual attention (e.g. saving a file to SharePoint).
		return <DebtorRemindersBanner />;
	}

	return (
		<Stack spacing={1}>
			<Typography variant='form_label' fontSize={'16px'}>
				Scheduled reminders
			</Typography>
			<DebtorRemindersBanner />
			{error ? (
				<Alert severity='warning' sx={{ alignItems: 'center' }}>
					Unable to load scheduled reminders.
				</Alert>
			) : reminders.length === 0 ? (
				<Typography variant='body2' color='text.secondary'>
					No scheduled reminders
				</Typography>
			) : (
				<TableContainer
					component={Paper}
					variant='outlined'
					sx={{ overflowX: 'auto', borderRadius: '8px' }}
				>
					<Table aria-label='Upcoming reminders'>
						<TableHead>
							<TableRow>
								<TableCell>Scheduled</TableCell>
								<TableCell>Method</TableCell>
								<TableCell>Invoice</TableCell>
								<TableCell>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{reminders.map((reminder) => (
								<TableRow key={reminder.id}>
									<TableCell>
										{formatScheduledFor(reminder.scheduledFor)}
									</TableCell>
									<TableCell>
										<Channel channel={reminder.channel} />
									</TableCell>
									<TableCell>{reminder.invoiceNumber || '—'}</TableCell>
									<TableCell>
										<ReminderStatusChip
											status={reminder.status}
											errorMessage={reminder.errorMessage}
										/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Stack>
	);
};

export default DebtorRemindersSection;
